import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { LightningIcon, TeacherIcon } from '@/common/icons/general.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { RootState } from '@/storage/store'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { SubmitButton } from './student_details/shared/submit-button'
import { useNavigate } from 'react-router-dom'
import validateApplicationSteps from '@/app/pages/family_application/step-checker/step_checker'
import { useEffect } from 'react'
import { format } from 'date-fns'

export default function NewFamilyAppLandingPage() {
  const { t: translate } = useTranslation()
  const programSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )
  const FamilyApplicationState = useSelector(
    (state: RootState) => state.FamilyApplication
  )

  const navigateTo = useNavigate()

  const getNextStep = () => {
    if (FamilyApplicationState.data != undefined) {
      const applicationSteps = validateApplicationSteps(
        FamilyApplicationState.data,
        programSettings.data!
      )

      const nextStep = Object.values(applicationSteps).find(
        (step) => step.status == 'not_completed'
      )

      if (nextStep) {
        return nextStep.urls.at(0) ?? ''
      }
    }
    return null
  }

  const getStepsCompletedCount = () => {
    if (FamilyApplicationState.data != undefined) {
      const applicationSteps = validateApplicationSteps(
        FamilyApplicationState.data,
        programSettings.data!
      )
      return applicationSteps == null
        ? 0
        : Object.values(applicationSteps).filter(
            (step) => step.status == 'completed'
          ).length
    }
    return 0
  }

  useEffect(() => {
    if (getStepsCompletedCount() > 0) navigateToNext()
  }, [])

  const navigateToNext = () => {
    const nextStep = getNextStep()
    if (nextStep) navigateTo(nextStep)
    else navigateTo(AppRoutePaths.family_application.review_application)
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{programSettings?.data?.name}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <section aria-labelledby="application-heading">
          <h2
            id="application-heading"
            className="text-xl font-medium text-gray-900 mb-10 sm:mb-5"
          >
            {translate('apply_for_a', {
              program_name: programSettings.data?.name,
              program_start_date: programSettings.data?.program_start_date
                ? format(new Date(programSettings.data?.program_start_date), 'yyyy')
                : '',
              program_end_date: programSettings.data?.program_end_date
                ? format(new Date(programSettings.data?.program_end_date), 'yyyy')
                : '',
            })}
          </h2>

          <div className="space-y-6">
            <section aria-labelledby="what-to-expect">
              <h3 id="what-to-expect" className="flex items-center font-medium">
                <TeacherIcon aria-hidden="true" />
                <span className="ml-2">{translate('what_to_expect')}</span>
              </h3>
              <p className="ml-6 mt-1 text-gray-700 text-sm">
                {translate('complete_this_application')}.
              </p>
            </section>
            <section aria-labelledby="what-you-will-need">
              <h3
                id="what-you-will-need"
                className="flex items-center font-medium"
              >
                <LightningIcon aria-hidden="true" />
                <span className="ml-2">{translate('what_you_will_need')}</span>
              </h3>
              <ul className="ml-6 mt-1 text-gray-700 text-sm space-y-1">
                <li>{translate('proof_of_identity')}</li>
                <li>{translate('proof_of_residency')}</li>
                <li>{translate('student_documentation')}</li>
                <li>{translate('proof_of_income')}</li>
                <li>{translate('if_income_verified')}</li>
              </ul>
            </section>
          </div>
        </section>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <SubmitButton
          buttonTxt={translate('start_application')}
          onClick={navigateToNext}
          status="idle"
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
